import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import moment from 'moment';
import { getInsurerRenewals } from '../../../../../api/requests';

interface RenewalsWithin30DaysProps {
  companyId?: number;
}

interface RenewalRow {
  policyNumber: string;
  insurerPolicyNumber: string;
  expiresOn: string;
}

/**
 * Renewals within 30 days — count plus the soonest-expiring policies for the insurer.
 */
const RenewalsWithin30Days: React.FC<RenewalsWithin30DaysProps> = ({ companyId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['getInsurerRenewals', companyId],
    queryFn: () => getInsurerRenewals(companyId, 30),
  });

  const payload: any = data ?? {};
  const count: number = payload?.data?.count ?? 0;
  const renewals: RenewalRow[] = payload?.data?.renewals ?? [];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 dark:bg-darkBg dark:border-darkBg-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-primary-main" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Renewals within 30 days</h3>
        </div>
        {!isLoading && (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            {count}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-100 dark:bg-darkBg-border rounded animate-pulse" />
          ))}
        </div>
      ) : renewals.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No policies due for renewal in the next 30 days.</p>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-darkBg-border">
          {renewals.slice(0, 8).map((row) => {
            const daysLeft = moment(row.expiresOn).startOf('day').diff(moment().startOf('day'), 'days');
            return (
              <div key={row.policyNumber} className="flex items-center justify-between py-2.5 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 dark:text-darkBg-mainText truncate">{row.policyNumber}</p>
                  {row.insurerPolicyNumber && (
                    <p className="text-xs text-gray-400 truncate">{row.insurerPolicyNumber}</p>
                  )}
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="text-gray-600 dark:text-gray-300">{moment(row.expiresOn).format('DD MMM YYYY')}</p>
                  <p className={`text-xs ${daysLeft <= 7 ? 'text-red-600' : 'text-amber-600'}`}>
                    {daysLeft <= 0 ? 'Due today' : `in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RenewalsWithin30Days;
